import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchesSearch } from '../../../shared/utils/text';
import { FILTER_ALL, RECORD_STATUS } from '../../../shared/constants';
import {
  createOfficeElectricityAssignment,
  createOffice,
  listElectricitySuppliers,
  listOfficeElectricityAssignments,
  listOffices,
  updateOfficeElectricityAssignment,
  updateOffice,
} from '../../../api/officesApi';
import { reportingCalendarYear } from '../../../shared/utils/reportingYear';
import {
  normalizeElectricitySuppliers,
  normalizeOfficeAssignment,
} from '../model/scope2Assignment';

/** `search` comes from the toolbar; the rest are the per-column header filters. */

const DEFAULT_FILTERS = {
  search: '',
  name: '',
  address: '',
  electricity: '',
  type: FILTER_ALL,
  assignment: FILTER_ALL,
  status: FILTER_ALL,
};

const matchesStatus = (office, status) => {
  if (status === FILTER_ALL) return true;
  return status === RECORD_STATUS.ACTIVE ? office.is_active : !office.is_active;
};

/** Compare canonical Office codes without depending on browser locale. */
export const compareOfficesByLocationCode = (leftOffice, rightOffice) => {
  const leftCode = String(leftOffice.location_code || '');
  const rightCode = String(rightOffice.location_code || '');
  if (leftCode === rightCode) return 0;
  return leftCode < rightCode ? -1 : 1;
};

/** List order: active offices first, then largest floor area first. */
export const compareOfficesForList = (leftOffice, rightOffice) => {
  const leftActive = leftOffice.is_active ? 0 : 1;
  const rightActive = rightOffice.is_active ? 0 : 1;
  if (leftActive !== rightActive) return leftActive - rightActive;
  const leftArea = Number(leftOffice.area_m2) || 0;
  const rightArea = Number(rightOffice.area_m2) || 0;
  if (leftArea !== rightArea) return rightArea - leftArea;
  return compareOfficesByLocationCode(leftOffice, rightOffice);
};

const assignmentOfficeId = assignment => (
  assignment?.office_ref_id || assignment?.office_id || null
);

const normalizeAssignmentRows = response => (
  Array.isArray(response) ? response : response?.items || []
);

export function mergeOfficeAssignments(offices, assignments, fiscalYear) {
  const assignmentByOfficeId = new Map(
    assignments.map(assignment => [assignmentOfficeId(assignment), assignment]),
  );
  return offices.map((office) => ({
    ...office,
    assignment_fiscal_year: fiscalYear,
    electricity_assignment: assignmentByOfficeId.get(office.id) || null,
  }));
}

export const createAssignmentBody = (officeId, assignment) => {
  return {
    office_id: officeId,
    effective_from_fiscal_year: assignment.effective_from_fiscal_year,
    supplier_id: assignment.supplier_id,
    application_code: assignment.application_code,
    evidence_reference: assignment.evidence_reference,
  };
};

export const updateAssignmentBody = (assignment, currentAssignment) => {
  return {
    effective_from_fiscal_year: Number(assignment.effective_from_fiscal_year),
    supplier_id: assignment.supplier_id,
    application_code: assignment.application_code,
    evidence_reference: assignment.evidence_reference,
    entity_version: currentAssignment.version,
  };
};


export default function useOfficeMaster() {
  const [offices, setOffices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [fiscalYear, setFiscalYear] = useState(reportingCalendarYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(25);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [officeRows, supplierRows, assignmentResponse] = await Promise.all([
        listOffices(),
        listElectricitySuppliers(),
        listOfficeElectricityAssignments(fiscalYear),
      ]);
      setSuppliers(normalizeElectricitySuppliers(supplierRows));
      setOffices(mergeOfficeAssignments(
        officeRows || [], normalizeAssignmentRows(assignmentResponse), fiscalYear,
      ));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);
  useEffect(() => {
    reload();
  }, [reload]);

  const reloadSuppliers = useCallback(async () => {
    const supplierRows = await listElectricitySuppliers();
    const normalized = normalizeElectricitySuppliers(supplierRows);
    setSuppliers(normalized);
    return normalized;
  }, []);
  const setFilter = useCallback((key, value) => {
    setFiltersState((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);
  const setRowsPerPage = useCallback((value) => {
    setRowsPerPageState(value);
    setPage(0);
  }, []);
  const visibleOffices = useMemo(
    () =>
      offices
        .filter((office) => matchesStatus(office, filters.status))
        .filter(
          (office) =>
            filters.type === FILTER_ALL || office.location_type === filters.type
        )
        .filter((office) =>
          matchesSearch(
            filters.name,
            office.location_name_en,
            office.location_name_jp
          )
        )
        .filter((office) =>
          matchesSearch(filters.address, office.location_address)
        )
        .filter((office) =>
          matchesSearch(
            filters.electricity,
            normalizeOfficeAssignment(office, fiscalYear).supplierName,
            normalizeOfficeAssignment(office, fiscalYear).registrationNo,
          )
        )
        .filter(
          (office) => {
            const assignment = normalizeOfficeAssignment(office, fiscalYear);
            return filters.assignment === FILTER_ALL
              || (assignment.isConfigured
                && assignment.applicationCode === filters.assignment);
          }
        )
        .filter((office) =>
          matchesSearch(
            filters.search,
            office.location_code,
            office.location_name_en,
            office.location_name_jp,
            office.location_address,
            normalizeOfficeAssignment(office, fiscalYear).supplierName,
            normalizeOfficeAssignment(office, fiscalYear).registrationNo,
          )
        )
        .sort(compareOfficesForList),
    [fiscalYear, offices, filters]
  );

  const saveAssignment = useCallback(async (officeId, existing, assignmentBody) => {
    if (!assignmentBody) return null;
    const currentAssignment = normalizeOfficeAssignment(existing, fiscalYear);
    if (!currentAssignment.id) {
      return createOfficeElectricityAssignment(
        createAssignmentBody(officeId, assignmentBody),
      );
    }
    return updateOfficeElectricityAssignment(
      currentAssignment.id,
      updateAssignmentBody(assignmentBody, currentAssignment),
    );
  }, [fiscalYear]);

  /** Save Office and its selected-year electricity assignment as one UI action. */
  const saveOffice = useCallback(
    async (existing, payload) => {
      const officeBody = { ...payload.office };
      if (existing) delete officeBody.location_code;
      let wasOfficeCommitted = false;
      try {
        const savedOffice = existing
          ? await updateOffice(existing.id, {
            ...officeBody,
            entity_version: existing.entity_version,
          })
          : await createOffice(officeBody);
        wasOfficeCommitted = true;
        const assignmentMutation = await saveAssignment(
          savedOffice.id,
          existing,
          payload.assignment,
        );
        if (assignmentMutation) {
          await reload();
          return { office: savedOffice, assignmentMutation };
        }
        const assignment = existing?.electricity_assignment || null;
        const mergedOffice = {
          ...savedOffice,
          assignment_fiscal_year: fiscalYear,
          electricity_assignment: assignment,
        };
        setOffices(previous => existing
          ? previous.map(office => (office.id === mergedOffice.id ? mergedOffice : office))
          : [...previous, mergedOffice]);
        return { office: mergedOffice, assignmentMutation };
      } catch (saveError) {
        if (wasOfficeCommitted || !existing || saveError.status === 409) await reload();
        if (wasOfficeCommitted) saveError.wasOfficeSaved = true;
        throw saveError;
      }
    },
    [fiscalYear, reload, saveAssignment]
  );
  return {
    offices: visibleOffices,
    suppliers,
    fiscalYear,
    setFiscalYear,
    loading,
    error,
    reload,
    reloadSuppliers,
    filters,
    setFilter,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    saveOffice,
  };
}
